import type { EditorState } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { findWrapping } from '@tiptap/pm/transform';

import { generateId, TIPTAP_LAYOUT_CELL_TYPE, TIPTAP_LAYOUT_TYPE } from './../../ricos-content';

import { balanceLayoutCells, MAX_COLUMNS, MIN_COL_SPAN } from './shared';

export const createCellNormalizingTransactionPlugin = () => {
  return new Plugin({
    key: new PluginKey('normalize-cell'),
    appendTransaction: (_, oldState: EditorState, newState: EditorState) => {
      if (newState.doc.eq(oldState.doc) && newState.selection.eq(oldState.selection)) {
        return;
      }

      const tr = newState.tr;

      tr.doc.forEach((node, pos) => {
        // All empty LAYOUTS must be removed
        if (
          node.type.name === TIPTAP_LAYOUT_TYPE &&
          node.childCount === 1 &&
          node.firstChild?.attrs.id === null
        ) {
          tr.deleteRange(pos, pos + node.nodeSize);
        }
      });

      tr.doc.descendants((node, pos, parent) => {
        // All CELLS must be wrapped in a LAYOUT
        if (
          node.type.name === TIPTAP_LAYOUT_CELL_TYPE &&
          parent?.type.name !== TIPTAP_LAYOUT_TYPE
        ) {
          const $from = tr.doc.resolve(pos);
          const $to = tr.doc.resolve(pos + node.nodeSize);
          const nodeRange = $from.blockRange($to);
          const wrapping =
            nodeRange &&
            findWrapping(nodeRange, newState.schema.nodes[TIPTAP_LAYOUT_TYPE], {
              id: generateId(),
            });

          if (!wrapping) {
            return;
          }

          tr.wrap(nodeRange, wrapping);
        }

        // All CELLS must have valid colSpan
        if (
          node.type.name === TIPTAP_LAYOUT_CELL_TYPE &&
          parent?.type.name === TIPTAP_LAYOUT_TYPE &&
          node.attrs.colSpan === null
        ) {
          const colSpans: number[] = [];
          parent.content.forEach(child => colSpans.push(child.attrs.colSpan ?? 0));
          const totalColSpan = colSpans.reduce((acc, curr) => acc + curr, 0);
          const remainingColumns = MAX_COLUMNS - totalColSpan;

          tr.setNodeAttribute(pos, 'colSpan', remainingColumns);
        }
      });

      tr.doc.forEach((node, pos) => {
        // All CELLS must not exceed the maximum number of columns
        if (node.type.name === TIPTAP_LAYOUT_TYPE) {
          const colSpans: number[] = [];
          node.content.forEach(child => colSpans.push(child.attrs.colSpan ?? 0));
          const allColSpansValid = colSpans.every(colSpan => colSpan >= MIN_COL_SPAN);
          const colSpansSum = colSpans.reduce((acc, curr) => acc + curr, 0);

          if (colSpansSum !== MAX_COLUMNS || !allColSpansValid) {
            balanceLayoutCells(tr, node, pos);
          }
        }
      });

      return tr;
    },
  });
};
